import { hasAccessTokenResolver } from "@devforce/trailhead-client-auth";
import * as env from "@devforce/trailhead-client-env";
import {
  LearningsFragment,
  LearningsFragmentType,
} from "@devforce/trailhead-client-gql";

import {
  CollectionQuery,
  getCollectionItem,
  getCommunityCollectionItem,
  CommunityNodeSummariesType,
  CommunityNodeSummaryType,
} from "common/contentCollection";
import { QueryAdapterValue, gql } from "common/graphqlAdapter";
import type { LearningKindCollectionType } from "common/learning";

export type LearningCollectionItem = {
  name: string;
  type: LearningKindCollectionType;
};

export type CommunityCollectionItem = string;
export type LearningQueryResult = QueryAdapterValue<{
  recentlyViewed: LearningsFragmentType;
}>;
export class LearningQuery implements CollectionQuery<LearningQueryResult> {
  options: {
    limit?: number;
    learningItems: LearningCollectionItem[];
  } = null!;

  useFetch = true;

  constructor(options: LearningQuery["options"]) {
    this.options = options;
  }

  private query = gql`
    ${LearningsFragment}
    query ThexternalLearningUnauthenticated(
      $first: Int
      $learnings: [LearningInput!]!
    ) {
      recentlyViewed(first: $first, learnings: $learnings) {
        ...Learnings
      }
    }
  `;

  private queryAuthenticated = gql`
    ${LearningsFragment}
    query ThexternalLearningAuthenticated(
      $first: Int
      $learnings: [LearningInput!]!
    ) {
      recentlyViewed(first: $first, learnings: $learnings) {
        ...Learnings
      }
    }
  `;

  getQueryOptions() {
    return {
      query: hasAccessTokenResolver() ? this.queryAuthenticated : this.query,
      variables: {
        first: this.options.limit,
        learnings: this.options.learningItems,
      },
    };
  }

  getCollectionItems(result: LearningQueryResult) {
    const edges = result.data.recentlyViewed.edges ?? [];
    return edges
      .map((edge) => edge!.node!)
      .map((learning) => getCollectionItem(learning));
  }

  hasMore(result: LearningQueryResult) {
    return result.data.recentlyViewed.pageInfo.hasNextPage;
  }
}

export type CommunityQueryResult = QueryAdapterValue<{
  communityNodeSummaries: CommunityNodeSummariesType;
}>;

export class CommunityQuery implements CollectionQuery<CommunityQueryResult> {
  options: {
    limit?: number;
    communityItems: string[];
  } = null!;

  useFetch = true;

  constructor(options: CommunityQuery["options"]) {
    this.options = options;
  }

  private query = gql`
    query ThexternalCommunity($ids: [ID!]!) {
      communityNodeSummaries(ids: $ids) {
        id
        type
        name
        typeLocalized
        avatar
        description
        url
      }
    }
  `;
  getQueryOptions() {
    return {
      context: {
        uri: env.get("COMMUNITY_API_HOST"),
      },
      query: this.query,
      variables: {
        ids: this.options.communityItems,
      },
    };
  }

  getCollectionItems(result: CommunityQueryResult) {
    const summaries = result.data.communityNodeSummaries ?? [];
    return summaries.map((communityItem: CommunityNodeSummaryType) =>
      getCommunityCollectionItem(communityItem)
    );
  }
}
